import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  Button,
  Combobox,
  Field,
  Input,
  MultiCombobox,
  type ComboboxOption,
} from '@/components/ui';
import {
  ACTION_SLUGS,
  HTTP_METHODS,
  MODULE_SLUGS,
  daysAgoLocal,
  nextDayLocal,
  todayLocal,
} from '@/lib/api-log-utils';
import { cx } from '@/lib/cx';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import type { ApiLogListParams, ApiLogSort, ApiLogStatusClass } from '@/lib/types';
import { useUserOptions } from '@/lib/use-user-options';

/*
 * The filter bank for the activity log.
 *
 * VOCABULARY, NOT WIRING. This screen reads as an activity feed for staff who
 * do not think in HTTP, so every label here is business English — "What
 * happened", "Result", "Who", "Area". The params underneath are unchanged and
 * every contract rule the older technical version encoded is still enforced
 * here; the comments name each one, because they are exactly the things a
 * relabelling pass tends to quietly break.
 *
 * Controlled: the page owns the URL and hands the whole param object down; this
 * component holds no filter state of its own. The only local state is UI
 * affordance — which disclosure is open, whether the user asked for an exact
 * status code, and the drafts behind the debounced text boxes.
 *
 * ✅ **EVERY DROPDOWN HERE IS A SEARCHABLE `Combobox` — owner's decision,
 * 2026-09-13.** *SUPERSEDED: seven native `ui/Select`s.* Consistency was chosen
 * explicitly over the "a three-option list needs no search box" argument, so
 * "Direction" is a Combobox for the same reason "Who" is. Do not convert one
 * back on the grounds that it is short.
 *
 * ✅ **What happened, Who, Area and HTTP method are `MultiCombobox`es (owner,
 * 2026-10-28)**: several values, one CSV param. Time window, Result, Signed in,
 * Sort and Direction stay single — each is a mode, a tri-state boolean, or (Result)
 * one writer over three mutually exclusive params.
 *
 * ⚠️ **THE `placeholder` / `emptyOptionLabel` RULE, because getting it wrong is
 * silent.** `Combobox` looks the selected row up in the `options` PROP, and the
 * `emptyOptionLabel` row is injected into the rendered list only — it is never
 * in `options`. So when a filter is cleared (`value === ''`) nothing matches and
 * the TRIGGER falls back to `placeholder`. The two must therefore be the SAME
 * STRING, or the closed control and the open list disagree about what "no
 * filter" is called. The converse: a control whose value is always a real member
 * (Time window) passes NEITHER, because its blank state is unreachable and a
 * placeholder naming one of the members would be a claim about a selection that
 * is not in force.
 *
 * Layout note (this has already broken the app once, see CLAUDE.md): a bank of
 * filter controls must NOT go in `PanelHeader`'s `actions` slot — that slot is
 * `shrink-0` and cannot wrap. This renders its own full-width row that wraps
 * freely, and every control sits in a `min-w-0` track so a long value cannot
 * widen the row past 320px.
 *
 * On `FilterSheet` (src/components/ui/ColumnFilter.tsx): considered as the
 * "More filters" primitive and rejected. It is built around `ColumnFilterProps`
 * — a per-column list of distinct values plus sort and emptiness, driven by
 * `useTableFilters` and applied CLIENT-side to the loaded page. These filters
 * are server-side query params of five different shapes (instants, integers,
 * enums, substrings, tri-state booleans), so it would have to be gutted to fit.
 * An inline disclosure is used instead, which also keeps every control in the
 * document flow at 320px.
 */

const DEBOUNCE_MS = 250;

/** Everything that narrows the result set. `page`, `perPage`, `sort` and `order` do not. */
const FILTER_KEYS: readonly (keyof ApiLogListParams)[] = [
  'from',
  'to',
  'userId',
  'actorEmail',
  'anonymous',
  'method',
  'moduleSlug',
  'actionSlug',
  'recordId',
  'route',
  'path',
  'requestId',
  'statusCode',
  'statusClass',
  'failed',
  'errorCode',
  'minDurationMs',
];

/**
 * The keys the always-visible controls own — Time window, Result, What
 * happened, Who. Everything else lives behind "More filters" and is counted on
 * its badge.
 *
 * `route` is the odd one out and is handled in `isPrimaryKey`: the Sign-ins
 * shortcut writes it from the primary row, but the advanced section also has a
 * route box, so whether it counts as hidden depends on its value.
 *
 * ⚠️ `actorEmail` IS DELIBERATELY ABSENT and used to be here. Its control moved
 * into More filters when "Who" became a staff picker, and a key whose only
 * control is hidden must be counted on the badge — otherwise a feed narrowed to
 * one deleted account shows no sign anywhere on screen that it is narrowed at
 * all, which reads as that account being the only thing in the window.
 */
const PRIMARY_KEYS: readonly (keyof ApiLogListParams)[] = [
  'from',
  'to',
  'failed',
  'statusCode',
  'statusClass',
  'actionSlug',
  'userId',
];

/**
 * The route the Sign-ins shortcut pins.
 *
 * `/auth` sits outside the permission model, so a sign-in logs `module: null`
 * and `action: null`. It cannot be reached through Area or What happened at
 * all — an exact route match is the only way, which is why this is a shortcut
 * rather than an option in either list.
 */
const AUTH_LOGIN_ROUTE = '/api/v1/auth/login';

const STATUS_CLASSES: readonly ApiLogStatusClass[] = ['1xx', '2xx', '3xx', '4xx', '5xx'];

/**
 * Business wording for the raw status classes, kept reachable and marked
 * technical.
 *
 * ⚠️ *SUPERSEDED 2026-09-13: "kept reachable but GROUPED as technical".* There
 * is no group any more — see `RESULT_OPTIONS` below, which carries the marker
 * per row because `Combobox` has no `<optgroup>`.
 */
const STATUS_CLASS_LABELS: Record<ApiLogStatusClass, string> = {
  '1xx': 'Informational (1xx)',
  '2xx': 'Success (2xx)',
  '3xx': 'Redirect (3xx)',
  '4xx': 'Rejected by the app (4xx)',
  '5xx': 'System error (5xx)',
};

/**
 * The three named results that are a single exact status code underneath.
 *
 * They are codes, not classes, so they go through the same exclusive writer and
 * cannot coexist with a class — see `setResult`.
 */
const NAMED_CODES: Record<string, number> = {
  refused: 403,
  missing: 404,
  invalid: 422,
};

/** The marker that replaced the `<optgroup>` label. One spelling, six rows. */
const TECHNICAL_HINT = 'Technical';

/**
 * The Result list as `Combobox` rows.
 *
 * 🔴 **THIS CONTROL HELD THE APP'S ONLY `<optgroup>`, AND THE GROUPING WAS
 * DOING REAL WORK.** *SUPERSEDED 2026-09-13: `<optgroup label="Technical">`
 * wrapping the five status classes and "Exact status code…".* `ComboboxOption`
 * is `{value, label, hint?}` and has no group concept at all, so the block label
 * had to become something a ROW carries or be lost.
 *
 * ⚠️ **`hint` IS THAT MARKER AND IT IS NOT DECORATION.** Flattened bare,
 * "System error (5xx)" sits in one list beside "Refused — no permission" and
 * reads as a sixth business outcome; the group label was the only thing saying
 * otherwise. Three properties make the per-row marker at least as strong as the
 * block label it replaces:
 *   • it renders on every technical row, so it cannot scroll away from the
 *     option it qualifies — a group label can;
 *   • it sits INSIDE the `role="option"` element, so it is part of that option's
 *     accessible name: a screen reader hears "System error (5xx), Technical" on
 *     every one of the six, where an `<optgroup>` announces its label once on
 *     entering the group;
 *   • `Combobox` filters on `label` + `hint`, so typing "technical" now narrows
 *     to exactly these six. The `<optgroup>` was never searchable.
 *
 * ⚠️ **A LABEL PREFIX ("Technical · System error (5xx)") WAS THE OTHER
 * CANDIDATE AND WAS REJECTED ON THE TRIGGER, NOT ON THE LIST.** `Combobox`
 * renders `selected.label` alone on the button and truncates it; this control is
 * `sm:w-52`, where "Technical · Rejected by the app (4xx)" clips to "Technical ·
 * Rejected b…" — the prefix would push the chosen value's own identity off the
 * control, which is the reverse of the failure being guarded against. `hint` is
 * not rendered on the trigger at all, so the CLOSED control reads exactly as it
 * always did — and so did a closed `<select>`, which never showed a group label
 * either. Nothing was lost at the trigger by flattening.
 *
 * ⚠️ **WHAT WAS LOST, SAID RATHER THAN BURIED:** one label above a block became
 * the word "Technical" on six consecutive rows — noisier — and there is no
 * `role="group"` nesting, so they are six peers sharing a marker rather than a
 * structurally nested set. `hint` is also spent here on a CATEGORY, where
 * everywhere else in this file it disambiguates an individual row (a colleague's
 * email). Accepted as the price of one searchable control; the distinction the
 * group existed to draw survives intact.
 *
 * Order is the old one exactly: business outcomes, then the technical block.
 * "Any result" is deliberately absent — `emptyOptionLabel` supplies it, above
 * everything, which is where the `<option value="">` sat.
 */
const RESULT_OPTIONS: ComboboxOption[] = [
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed — anything that did not work' },
  { value: 'refused', label: 'Refused — no permission' },
  { value: 'missing', label: 'Not found' },
  { value: 'invalid', label: 'Rejected — invalid details' },
  ...STATUS_CLASSES.map((statusClass) => ({
    value: statusClass,
    label: STATUS_CLASS_LABELS[statusClass],
    hint: TECHNICAL_HINT,
  })),
  { value: 'code', label: 'Exact status code…', hint: TECHNICAL_HINT },
];

/**
 * Plain English for the permission action slugs.
 *
 * A one-to-one map, which is the whole reason "What happened" writes
 * `actionSlug` rather than `method` — see the note above that control.
 */
const ACTION_LABELS: Record<string, string> = {
  read: 'Viewed',
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  convert: 'Converted',
  export: 'Exported',
};

/** The same six as rows. "Anything" is `emptyOptionLabel`, not a member here. */
const ACTION_OPTIONS: ComboboxOption[] = ACTION_SLUGS.map((slug) => ({
  value: slug,
  label: ACTION_LABELS[slug] ?? slug,
}));

/**
 * The HTTP verbs, as themselves.
 *
 * No business wording, deliberately: inventing one is the whole job of the "What
 * happened" control, which writes a DIFFERENT param. See the note at that
 * control before pairing these two up. "Any method" is `emptyOptionLabel`.
 */
const METHOD_OPTIONS: ComboboxOption[] = HTTP_METHODS.map((method) => ({
  value: method,
  label: method,
}));

/** Plain English for the permission module slugs. An unknown slug falls back to itself. */
/**
 * Business words for each module slug the audit can stamp.
 *
 * ⚠️ **Keyed on `string`, and the lookup falls back to the raw slug**, so a
 * missing entry degrades to `sales-tracker` rather than to a blank row — which
 * is why this map going short is survivable where `MODULE_SLUGS` going short
 * is not. It is filled to match that array all the same: a dropdown that reads
 * half in words and half in slugs looks broken.
 *
 * ⚠️ **`invoice-tracker` is labelled "Future billables" ON PURPOSE.** The page
 * was renamed and the SLUG deliberately did not move with it, so the audit
 * stamps the old word for the screen everybody now calls the new one. The slug
 * is the join key; this is the label. Do not "correct" either to match the
 * other.
 */
const MODULE_LABELS: Record<string, string> = {
  deals: 'Deals',
  quotes: 'Quotes',
  invoices: 'Invoices',
  'credit-notes': 'Credit notes',
  deliverables: 'Deliverables',
  'client-deliverables': 'Client deliverables',
  companies: 'Companies',
  contacts: 'Contacts',
  projects: 'Projects',
  products: 'Products',
  cycles: 'Cycles',
  intakes: 'Intakes',
  tasks: 'Tasks',
  notes: 'Notes',
  'sales-tracker': 'Sales tracker',
  'invoice-tracker': 'Future billables',
  'revenue-tracker': 'Revenue tracker',
  'accounts-dashboard': 'Accounts dashboard',
  targets: 'Targets',
  'target-periods': 'Target periods',
  commissions: 'Commissions',
  'event-interest': 'Event interest',
  'event-maps': 'Event maps',
  docusign: 'DocuSign',
  users: 'Staff accounts',
  'reference-data': 'Reference data',
  settings: 'Settings',
  preferences: 'Preferences',
  'onboarding-templates': 'Onboarding templates',
  'api-keys': 'API keys',
  'support-sessions': 'Support sessions',
  public: 'Public API',
  'api-logs': 'Activity log',
};

const SORT_LABELS: Record<ApiLogSort, string> = {
  createdAt: 'When it happened',
  durationMs: 'Time taken',
  statusCode: 'Result code',
};

/** The three sortable columns as rows. "Default" — the key absent — is `emptyOptionLabel`. */
const SORT_OPTIONS: ComboboxOption[] = (Object.keys(SORT_LABELS) as ApiLogSort[]).map((key) => ({
  value: key,
  label: SORT_LABELS[key],
}));

/** `order` is only ever `asc` or `desc`; "Default" is the absent key, via `emptyOptionLabel`. */
const DIRECTION_OPTIONS: ComboboxOption[] = [
  { value: 'desc', label: 'Newest / largest first' },
  { value: 'asc', label: 'Oldest / smallest first' },
];

/**
 * The two real states of the tri-state "Signed in" filter.
 *
 * ⚠️ The third — "Anyone", meaning the key is omitted — is `emptyOptionLabel`
 * and must NOT be listed here as a row with `value: 'false'` or similar. A
 * boolean filter has no "false means any"; see `triState` / `triValue`.
 */
const ANONYMOUS_OPTIONS: ComboboxOption[] = [
  { value: 'false', label: 'Signed-in staff only' },
  { value: 'true', label: 'Not signed in' },
];

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const HOUR_MS = 3_600_000;

/* ------------------------------------------------------------------ *
 * Small helpers
 * ------------------------------------------------------------------ */

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/*
 * Multi-value params (owner, 2026-10-28): What happened, Who, Area and HTTP
 * method each hold a comma-separated list in ONE param. `undefined` is "all".
 */
function listOf(value: string | number | undefined): string[] {
  if (value === undefined) return [];
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
}

function csvOrUndefined(values: readonly string[]): string | undefined {
  return values.length === 0 ? undefined : values.join(',');
}

/** A real instant, for the sub-day presets. Not a calendar date — never `slice(0, 10)` this. */
function instantAgo(ms: number): string {
  return new Date(Date.now() - ms).toISOString();
}

/** The calendar day a stored `from` falls on, for seeding the custom-range box. */
function dayPart(value: string | undefined): string {
  if (!value) return '';
  return DAY_PATTERN.test(value) ? value : value.slice(0, 10);
}

/**
 * The inverse of `nextDayLocal`, used only to redisplay a stored `to` as the
 * end DAY the user picked. `api-log-utils` exports no inverse, and this only
 * ever runs over a value this control itself produced.
 */
function previousDay(ymd: string): string {
  const parts = ymd.split('-').map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return '';
  const [y, m, d] = parts as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
}

/** Undo the `nextDayLocal` we send, so the box shows the day the user chose. */
function endDayOf(to: string | undefined): string {
  if (!to || !DAY_PATTERN.test(to)) return '';
  return previousDay(to);
}

/* ------------------------------------------------------------------ *
 * Debounced text drafts
 * ------------------------------------------------------------------ */

/**
 * A local mirror of one URL-backed string, flushed upward after 250ms — the
 * house convention, so a filtered list is not refetched per keystroke.
 *
 * `settledRef` holds the last value the two sides agreed on. That is what lets
 * an external change (Reset, a deep link, the back button) overwrite the draft
 * while the echo of our own commit does not clobber what the user is typing.
 */
function useDebouncedDraft(
  external: string,
  commit: (next: string) => void,
): readonly [string, (next: string) => void] {
  const [draft, setDraft] = useState(external);
  const settledRef = useRef(external);
  const commitRef = useRef(commit);

  useEffect(() => {
    commitRef.current = commit;
  });

  useEffect(() => {
    if (external === settledRef.current) return;
    settledRef.current = external;
    setDraft(external);
  }, [external]);

  useEffect(() => {
    if (draft === settledRef.current) return;
    const timer = setTimeout(() => {
      settledRef.current = draft;
      commitRef.current(draft);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  return [draft, setDraft] as const;
}

interface TextFilterProps {
  label: string;
  placeholder?: string;
  value: string | undefined;
  /** Receives `undefined` for an empty box — never an empty string. */
  onCommit: (next: string | undefined) => void;
  disabled: boolean;
  type?: 'text' | 'search';
  leadingIcon?: ReactNode;
}

function TextFilter({
  label,
  placeholder,
  value,
  onCommit,
  disabled,
  type = 'text',
  leadingIcon,
}: TextFilterProps) {
  const commit = useCallback(
    (next: string) => {
      const trimmed = next.trim();
      // Omit the key rather than sending `?path=`. An empty query param is not a
      // default on this API — it coerces and 422s.
      onCommit(trimmed === '' ? undefined : trimmed);
    },
    [onCommit],
  );
  const [draft, setDraft] = useDebouncedDraft(value ?? '', commit);

  return (
    <Field label={label}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Input
          id={id}
          type={type}
          aria-describedby={describedBy}
          placeholder={placeholder}
          disabled={disabled}
          leadingIcon={leadingIcon}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
      )}
    </Field>
  );
}

interface NumberFilterProps {
  label: string;
  placeholder?: string;
  value: number | undefined;
  onCommit: (next: number | undefined) => void;
  disabled: boolean;
}

/**
 * Digits only. Non-numeric characters are dropped as they are typed rather than
 * parsed away at commit time — `?minDurationMs=NaN` is a 422, and a box that
 * silently empties itself a quarter-second later is worse than one that refuses
 * the key.
 */
function NumberFilter({ label, placeholder, value, onCommit, disabled }: NumberFilterProps) {
  const commit = useCallback(
    (next: string) => {
      onCommit(next === '' ? undefined : Number(next));
    },
    [onCommit],
  );
  const [draft, setDraft] = useDebouncedDraft(value === undefined ? '' : String(value), commit);

  return (
    <Field label={label}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Input
          id={id}
          type="text"
          inputMode="numeric"
          aria-describedby={describedBy}
          placeholder={placeholder}
          disabled={disabled}
          value={draft}
          onChange={(event) => setDraft(event.target.value.replace(/[^0-9]/g, ''))}
        />
      )}
    </Field>
  );
}

/* ------------------------------------------------------------------ *
 * Result: one control over three params, two of them mutually exclusive
 * ------------------------------------------------------------------ */

/**
 * The shape that makes "both set" unrepresentable.
 *
 * `statusCode` and `statusClass` are mutually exclusive on the API — sending
 * both is a 422, deliberately, because `statusCode=404&statusClass=5xx` is not
 * an AND: it matches nothing, and an empty page reads as "no such requests
 * happened". So there is one control, one writer, and a union that cannot
 * express the illegal state.
 *
 * `failed` is folded into the same union rather than kept as its own select.
 * The API does allow `failed` alongside a code or a class, but every such
 * combination is either redundant ("Failed" + 4xx) or contradictory
 * ("Succeeded" + 5xx, which returns nothing and reads like a quiet week). One
 * business question — how did it turn out — is one control.
 */
type ResultChoice =
  | { kind: 'any' }
  | { kind: 'outcome'; failed: boolean }
  | { kind: 'class'; statusClass: ApiLogStatusClass }
  | { kind: 'code'; statusCode: number | undefined };

/**
 * Which option a stored result state corresponds to.
 *
 * A code or class wins over `failed` when they somehow arrive together (a
 * hand-edited URL): the code is the more specific claim, and the next selection
 * through `setResult` normalises all three keys anyway.
 */
function resultSelectValue(value: ApiLogListParams, wantsExactCode: boolean): string {
  if (value.statusClass !== undefined) return value.statusClass;
  if (value.statusCode !== undefined) {
    const named = Object.keys(NAMED_CODES).find((key) => NAMED_CODES[key] === value.statusCode);
    return named ?? 'code';
  }
  if (wantsExactCode) return 'code';
  if (value.failed !== undefined) return value.failed ? 'failed' : 'succeeded';
  return '';
}

/* ------------------------------------------------------------------ *
 * Date window
 * ------------------------------------------------------------------ */

type DateMode = 'any' | 'hour' | 'day' | 'week' | 'month' | 'custom';

/**
 * ⛔ **NO `emptyOptionLabel` AND NO `placeholder` ON THIS ONE — it is the only
 * dropdown in this bank without them, and adding either breaks it silently.**
 *
 * "Any time" is `DateMode`'s own `'any'` member: a real value this component
 * holds in local state, NOT the absent-key blank every other filter here writes.
 * `emptyOptionLabel` would inject a SECOND row also meaning "any" whose value is
 * `''` — which matches no `DateMode`, so picking it would set `dateMode` to a
 * value outside its own union, and the trigger (which looks the selection up in
 * `options`) would then render its placeholder over a live window.
 *
 * ⚠️ `'custom'` also REVEALS the From/To boxes, so this list is a disclosure as
 * well as a filter — see the control itself.
 */
const DATE_MODE_OPTIONS: ComboboxOption[] = [
  { value: 'any', label: 'Any time' },
  { value: 'hour', label: 'Last hour' },
  { value: 'day', label: 'Last 24 hours' },
  { value: 'week', label: 'Last 7 days' },
  { value: 'month', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom dates…' },
];

/**
 * Which preset a stored window corresponds to.
 *
 * The two day presets are recognisable because `daysAgoLocal` is deterministic.
 * The two sub-day ones are instants stamped at click time, so a reloaded page
 * shows them as a custom range — honest, since that is exactly what the URL now
 * holds. While the user stays put the locally-held mode survives; see the
 * resync guard in the component.
 */
function modeFromWindow(from: string | undefined, to: string | undefined): DateMode {
  if (from === undefined && to === undefined) return 'any';
  if (to === undefined && from === daysAgoLocal(7)) return 'week';
  if (to === undefined && from === daysAgoLocal(30)) return 'month';
  return 'custom';
}

/* ------------------------------------------------------------------ *
 * The component
 * ------------------------------------------------------------------ */

export interface ApiLogFiltersProps {
  value: ApiLogListParams;
  /** A partial patch. `undefined` for a key means "clear this filter". */
  onChange: (patch: Partial<ApiLogListParams>) => void;
  onReset: () => void;
  /** Disable while a fetch is in flight. */
  busy: boolean;
}

/**
 * Note for the host page: a patch here never carries `page`. Resetting to page
 * 1 on a filter change is the page's business, since it owns pagination — and
 * `page` must be a real number or absent, because `?page=` coerces to 0 and
 * 422s rather than falling back to a default.
 */
export function ApiLogFilters({ value, onChange, onReset, busy }: ApiLogFiltersProps): ReactElement {
  const advancedId = useId();
  const [expanded, setExpanded] = useRememberedState('filters-expanded', false);

  const signInsActive = value.route === AUTH_LOGIN_ROUTE;

  /* ---- active counts ---- */

  /*
   * `route` counts as primary only while it holds the shortcut's value, because
   * that is the one case where the user can see it set from the visible row. A
   * route typed into the advanced box is genuinely hidden and must show on the
   * badge.
   */
  const isPrimaryKey = useCallback(
    (key: keyof ApiLogListParams) =>
      PRIMARY_KEYS.includes(key) || (key === 'route' && signInsActive),
    [signInsActive],
  );

  const activeCount = FILTER_KEYS.filter((key) => value[key] !== undefined).length;
  const advancedCount = FILTER_KEYS.filter(
    (key) => !isPrimaryKey(key) && value[key] !== undefined,
  ).length;

  /* ---- result: the single writer of failed / statusCode / statusClass ---- */

  /*
   * "Exact status code…" was chosen but nothing typed yet — the one bit of the
   * Result control the URL cannot carry, since an empty `statusCode` is simply
   * absent.
   *
   * It needs no effect to stay honest: `resultSelectValue` lets a live
   * `statusClass` win over it, and every writer that could set a class (the
   * select handler and `handleReset`) clears it in the same turn.
   */
  const [wantsExactCode, setWantsExactCode] = useState(false);

  /**
   * The ONLY place any of the three result keys is written, and it always
   * writes ALL THREE — at least two of them to `undefined`. No code path can
   * leave `statusCode` and `statusClass` set together, and `ResultChoice`
   * cannot even describe one.
   */
  const setResult = useCallback(
    (choice: ResultChoice) => {
      onChange({
        failed: choice.kind === 'outcome' ? choice.failed : undefined,
        statusCode: choice.kind === 'code' ? choice.statusCode : undefined,
        statusClass: choice.kind === 'class' ? choice.statusClass : undefined,
      });
    },
    [onChange],
  );

  const resultValue = resultSelectValue(value, wantsExactCode);
  const showExactCode = resultValue === 'code';

  function selectResult(next: string) {
    if (next === '') {
      setWantsExactCode(false);
      setResult({ kind: 'any' });
      return;
    }
    if (next === 'succeeded' || next === 'failed') {
      setWantsExactCode(false);
      setResult({ kind: 'outcome', failed: next === 'failed' });
      return;
    }
    if (next === 'code') {
      setWantsExactCode(true);
      setResult({ kind: 'code', statusCode: undefined });
      return;
    }
    const named = NAMED_CODES[next];
    if (named !== undefined) {
      setWantsExactCode(false);
      setResult({ kind: 'code', statusCode: named });
      return;
    }
    setWantsExactCode(false);
    setResult({ kind: 'class', statusClass: next as ApiLogStatusClass });
  }

  /* ---- who: a staff picker, plus the email box for a deleted account ---- */

  /*
   * Two params, one question, in two slots.
   *
   * ⚠️ THIS WAS ONE FREE-TEXT BOX that read digits as a staff id and anything
   * else as an email. It was correct and it was unusable: nobody knows their
   * colleagues' staff ids, so the only reachable half was the email, and the
   * primary way to ask "what did this person do" was to guess a number. The
   * picker answers that. The email box stays because it is the ONLY way to
   * reach a DELETED user's activity — `user_id` is nulled on delete while
   * `actor_email` survives as the copy written at request time.
   *
   * ⚠️ EACH SLOT WRITES BOTH KEYS EVERY TIME, the other as `undefined`, so the
   * two can never disagree about who is being asked for — the invariant the
   * single box used to hold structurally. They would AND legally on the API,
   * and an id ANDed with a different person's email silently returns nothing:
   * an empty feed that reads as "this person did nothing" rather than as two
   * filters fighting.
   */
  const staff = useUserOptions();

  const selectedUserIds = useMemo(() => listOf(value.userId), [value.userId]);

  const staffOptions = useMemo<ComboboxOption[]>(() => {
    const options: ComboboxOption[] = staff.users.map((user) => ({
      value: String(user.id),
      label: user.name.trim() === '' ? user.email : user.name,
      hint: user.email,
    }));

    /*
     * The stored value, injected back when the directory does not hold it —
     * the same treatment every retired picklist value gets in this app. A
     * filter pinned to somebody since deleted, or sitting past the end of a
     * directory that has outgrown one page, would otherwise render as an empty
     * picker that silently drops the filter on the next selection.
     */
    for (const id of selectedUserIds) {
      if (options.some((o) => o.value === id)) continue;
      options.unshift({
        value: id,
        label: `Staff #${id}`,
        hint: 'Not on the current staff list',
      });
    }
    return options;
  }, [staff.users, selectedUserIds]);

  /*
   * Never a bare empty list. A failed read is "we do not know who is on staff",
   * and a picker rendering that as "no colleagues" is a statement about the
   * company made on the strength of a request that never came back.
   */
  const staffFootnote = staff.failed
    ? 'The staff list could not be read, so this picker is incomplete. Filter by email under More filters instead.'
    : staff.hasMore
      ? `Showing the first ${staff.users.length}. Somebody further down the directory is not listed.`
      : undefined;

  const commitUser = useCallback(
    (next: string[]) => {
      onChange({
        userId: csvOrUndefined(next),
        actorEmail: undefined,
      });
    },
    [onChange],
  );

  const commitActorEmail = useCallback(
    (next: string | undefined) => {
      onChange({ actorEmail: next, userId: undefined });
    },
    [onChange],
  );

  /* ---- date window ---- */

  const windowKey = `${value.from ?? ''}|${value.to ?? ''}`;
  const [dateMode, setDateMode] = useState<DateMode>(() => modeFromWindow(value.from, value.to));
  const [customFrom, setCustomFrom] = useState(() => dayPart(value.from));
  const [customTo, setCustomTo] = useState(() => endDayOf(value.to));
  const emittedWindowRef = useRef(windowKey);

  const applyWindow = useCallback(
    (from: string | undefined, to: string | undefined) => {
      emittedWindowRef.current = `${from ?? ''}|${to ?? ''}`;
      onChange({ from, to });
    },
    [onChange],
  );

  // Only re-derive when the window changed somewhere else. Without the guard the
  // echo of our own patch would knock "Last hour" back to "Custom range".
  useEffect(() => {
    if (windowKey === emittedWindowRef.current) return;
    emittedWindowRef.current = windowKey;
    const [nextFrom, nextTo] = windowKey.split('|');
    setDateMode(modeFromWindow(nextFrom || undefined, nextTo || undefined));
    setCustomFrom(dayPart(nextFrom || undefined));
    setCustomTo(endDayOf(nextTo || undefined));
  }, [windowKey]);

  /*
   * `to` is INCLUSIVE (`created_at <= :to`) and a bare date is UTC midnight, so
   * "2026-08-24" as `to` means "up to the very start of the 24th" and hides the
   * whole day the user just asked for. Sending the NEXT midnight is what makes a
   * picked end day actually mean that end day.
   */
  function toParamFor(endDay: string): string | undefined {
    return endDay === '' ? undefined : nextDayLocal(endDay);
  }

  // `from > to` is a 422 with `path: ["to"]`. Caught here so the user sees it on
  // the field instead of as a failed request; the patch is withheld until the
  // pair makes sense again.
  const rangeError =
    customFrom !== '' && customTo !== '' && customFrom > customTo
      ? 'The end date is before the start date.'
      : undefined;

  function selectDateMode(next: DateMode) {
    setDateMode(next);
    switch (next) {
      case 'any':
        setCustomFrom('');
        setCustomTo('');
        applyWindow(undefined, undefined);
        return;
      case 'hour':
        applyWindow(instantAgo(HOUR_MS), undefined);
        return;
      case 'day':
        applyWindow(instantAgo(24 * HOUR_MS), undefined);
        return;
      case 'week':
        applyWindow(daysAgoLocal(7), undefined);
        return;
      case 'month':
        applyWindow(daysAgoLocal(30), undefined);
        return;
      case 'custom':
        // Keep whatever the previous preset resolved to, rounded to its day, so
        // switching to Custom does not silently widen the window to everything.
        applyWindow(customFrom || undefined, toParamFor(customTo));
        return;
    }
  }

  function changeCustomFrom(day: string) {
    setCustomFrom(day);
    if (day !== '' && customTo !== '' && day > customTo) return;
    applyWindow(day || undefined, toParamFor(customTo));
  }

  function changeCustomTo(day: string) {
    setCustomTo(day);
    if (day !== '' && customFrom !== '' && customFrom > day) return;
    applyWindow(customFrom || undefined, toParamFor(day));
  }

  /* ---- the sign-ins shortcut ---- */

  /*
   * THE AREA TRAP, and the fix chosen for it.
   *
   * `/auth`, `/health` and the permission routes belong to no permission
   * module, so they log `module: null` and `action: null`. Picking any Area — or
   * any "What happened" — therefore removes every sign-in from the results. A
   * business user hunting "who signed in last week" would narrow the screen and
   * watch the answer disappear.
   *
   * A warning was the other option offered and was rejected: it tells someone
   * their search is wrong without telling them what to do instead, and the thing
   * to do (an exact route match on a path they would have to know) is not
   * discoverable from the screen. A one-click shortcut answers the question
   * directly. The Area field keeps its explanatory hint, which now points here.
   *
   * Turning it on clears Area and What happened, because both are null on a
   * sign-in row and either one would guarantee an empty page.
   */
  function toggleSignIns() {
    if (signInsActive) {
      onChange({ route: undefined });
      return;
    }
    onChange({ route: AUTH_LOGIN_ROUTE, moduleSlug: undefined, actionSlug: undefined });
  }

  /* ---- reset ---- */

  function handleReset() {
    setWantsExactCode(false);
    setDateMode('any');
    setCustomFrom('');
    setCustomTo('');
    emittedWindowRef.current = '|';
    onReset();
  }

  /* ---- option lists ---- */

  const moduleOptions = useMemo<ComboboxOption[]>(
    () => MODULE_SLUGS.map((slug) => ({ value: slug, label: MODULE_LABELS[slug] ?? slug })),
    [],
  );

  /** Tri-state boolean: "" omits the key. A boolean filter has no "false means any". */
  function triState(next: string): boolean | undefined {
    return next === '' ? undefined : next === 'true';
  }

  function triValue(current: boolean | undefined): string {
    return current === undefined ? '' : String(current);
  }

  const today = todayLocal();

  return (
    <div className="flex flex-col gap-3 border-b border-border px-4 py-3">
      {/* Row one: the four questions a business user actually asks — when, how it
        * turned out, what was done, and by whom. Wraps to a single column at
        * 320px; every track is `min-w-0` so a long email or a wide native date
        * picker scrolls inside itself rather than pushing the page sideways. */}
      <div className="flex flex-wrap items-start gap-2">
        {/*
          * ⚠️ **A DISCLOSURE AS WELL AS A FILTER: `custom` reveals the two date
          * boxes below.** `Combobox` commits once per selection, on the chosen
          * row, exactly as the native select did — there is no intermediate or
          * per-keystroke value — so `selectDateMode` still runs once per choice
          * and still CLEARS `customFrom`/`customTo` on the way to `any`. Picking
          * one of the presets leaves those two days in local state untouched and
          * merely hides the boxes, which is the behaviour a reader who flips
          * back to Custom expects.
          *
          * See `DATE_MODE_OPTIONS` for why this control alone carries neither
          * `emptyOptionLabel` nor `placeholder`.
          */}
        <div className="w-full min-w-0 sm:w-44">
          <Field label="Time window">
            {({ id }) => (
              <Combobox
                id={id}
                disabled={busy}
                value={dateMode}
                onChange={(next) => selectDateMode(next as DateMode)}
                options={DATE_MODE_OPTIONS}
                searchPlaceholder="Search time windows…"
              />
            )}
          </Field>
        </div>

        {dateMode === 'custom' && (
          <>
            <div className="w-full min-w-0 sm:w-40">
              <Field label="From">
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    type="date"
                    aria-describedby={describedBy}
                    max={today}
                    disabled={busy}
                    value={customFrom}
                    onChange={(event) => changeCustomFrom(event.target.value)}
                  />
                )}
              </Field>
            </div>
            <div className="w-full min-w-0 sm:w-40">
              <Field label="To" error={rangeError}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <Input
                    id={id}
                    type="date"
                    aria-describedby={describedBy}
                    invalid={Boolean(rangeError)}
                    max={today}
                    disabled={busy}
                    value={customTo}
                    onChange={(event) => changeCustomTo(event.target.value)}
                  />
                )}
              </Field>
            </div>
          </>
        )}

        {/*
          * ONE control for three params, two of which the API refuses together.
          * Picking a class clears any exact code and vice versa, in the same
          * patch — see `setResult`. The technical classes stay reachable, so a
          * deep link carrying one is still displayable.
          *
          * ⚠️ *SUPERSEDED 2026-09-13: "…stay reachable IN THEIR OWN GROUP…".*
          * The `<optgroup>` is gone with the native select; the rows carry the
          * marker themselves now. **Read `RESULT_OPTIONS` before touching this
          * list** — it records what the grouping was doing and why a per-row
          * `hint` was chosen over a label prefix.
          *
          * ⚠️ **A DISCLOSURE TOO: `code` reveals the exact-status-code box
          * below.** That reveal is driven by `resultValue`, which is derived
          * from the params plus `wantsExactCode`, not by this control's own
          * state — so it survives the swap untouched, and choosing away from
          * `code` still clears both the flag and the stored code through the one
          * writer.
          */}
        <div className="w-full min-w-0 sm:w-52">
          <Field label="Result">
            {({ id }) => (
              <Combobox
                id={id}
                disabled={busy}
                value={resultValue}
                onChange={selectResult}
                options={RESULT_OPTIONS}
                emptyOptionLabel="Any result"
                placeholder="Any result"
                searchPlaceholder="Search results…"
              />
            )}
          </Field>
        </div>

        {showExactCode && (
          <div className="w-full min-w-0 sm:w-36">
            <NumberFilter
              label="Status code"
              value={value.statusCode}
              disabled={busy}
              onCommit={(next) => setResult({ kind: 'code', statusCode: next })}
            />
          </div>
        )}

        {/*
          * METHOD vs ACTION — they are DIFFERENT server-side params and both
          * still exist. This control writes `actionSlug` ONLY, and never
          * `method`.
          *
          * Why action: the six action slugs map one-to-one onto the words a
          * business user wants (read/create/update/delete/convert/export →
          * Viewed/Created/Updated/Deleted/Converted/Exported), and two of them —
          * convert and export — have no HTTP verb that implies them at all.
          * Mapping onto `method` instead would be lossy in the other direction:
          * "Updated" is both PATCH and PUT, the param takes a single value, so
          * one of them would have to be silently dropped.
          *
          * `method` is NOT merged away and NOT written from here. It keeps its
          * own control in the advanced section under an honestly technical
          * label, where it ANDs with this one exactly as the API intends. No
          * patch ever carries both keys.
          */}
        <div className="w-full min-w-0 sm:w-44">
          <Field label="What happened">
            {({ id, 'aria-describedby': describedBy }) => (
              <MultiCombobox
                id={id}
                aria-describedby={describedBy}
                disabled={busy}
                values={listOf(value.actionSlug)}
                onChange={(next) => onChange({ actionSlug: csvOrUndefined(next) })}
                options={ACTION_OPTIONS}
                placeholder="Anything"
                searchPlaceholder="Search activity…"
                summaryNoun={{ singular: 'action', plural: 'actions' }}
              />
            )}
          </Field>
        </div>

        <div className="w-full min-w-0 sm:w-64 sm:flex-1">
          <Field label="Who">
            {({ id, 'aria-describedby': describedBy }) => (
              <MultiCombobox
                id={id}
                aria-describedby={describedBy}
                disabled={busy}
                loading={staff.loading}
                values={selectedUserIds}
                onChange={commitUser}
                options={staffOptions}
                placeholder="Anyone"
                searchPlaceholder="Search people…"
                summaryNoun={{ singular: 'person', plural: 'people' }}
                {...(staffFootnote !== undefined ? { footnote: staffFootnote } : {})}
              />
            )}
          </Field>
        </div>
      </div>

      {/* Row two: the sign-ins shortcut, the disclosure, the active count, and Reset. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={advancedId}
          leadingIcon={<ChevronIcon open={expanded} />}
        >
          More filters
          {advancedCount > 0 && (
            /* A number, not a dot: the count is the signal and the tint only
             * reinforces it — colour is never the only signal here. */
            <span
              aria-hidden="true"
              className="grid h-4 min-w-4 place-items-center rounded-full bg-surface-active px-1 text-2xs font-semibold text-fg tabular-nums"
            >
              {advancedCount}
            </span>
          )}
        </Button>

        {/* The pressed state carries a tick, and `aria-pressed` says the same
          * thing to a screen reader. Neutral, not the accent fill (2026-10-11
          * UI rework: the accent is for primary actions, nav and focus). */}
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          aria-pressed={signInsActive}
          onClick={toggleSignIns}
          leadingIcon={signInsActive ? <CheckIcon /> : undefined}
        >
          Sign-ins only
        </Button>

        <p aria-live="polite" className="min-w-0 flex-1 text-xs text-fg-muted">
          {activeCount === 0
            ? 'No filters applied'
            : `${plural(activeCount, 'filter', 'filters')} applied`}
        </p>

        <Button variant="ghost" size="sm" disabled={busy || activeCount === 0} onClick={handleReset}>
          Reset
        </Button>
      </div>

      {expanded && (
        <div
          id={advancedId}
          className="grid gap-4 border-t border-border pt-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          {/*
            * Area is the module slug from the permission model.
            *
            * /auth, /health and the permission endpoints belong to NO module, so
            * choosing one here hides them entirely — sign-ins included. The
            * Sign-ins button above is the way to those rows.
            *
            * ⚠️ **THE HINT THAT SAID SO IS GONE — owner's ruling, 2026-09-05**,
            * with the rest of this app's helper text. The exclusion is still
            * real and still silent, so the button above is now the only thing
            * pointing at those rows.
            */}
          <FilterGroup title="Where">
            <Field label="Area">
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  aria-describedby={describedBy}
                  values={listOf(value.moduleSlug)}
                  options={moduleOptions}
                  placeholder="Any area"
                  searchPlaceholder="Search areas…"
                  summaryNoun={{ singular: 'area', plural: 'areas' }}
                  disabled={busy}
                  onChange={(next) => onChange({ moduleSlug: csvOrUndefined(next) })}
                />
              )}
            </Field>
            <TextFilter
              label="Record reference"
              value={value.recordId}
              disabled={busy}
              onCommit={(next) => onChange({ recordId: next })}
            />
          </FilterGroup>

          <FilterGroup title="Who">
            <Field label="Signed in">
              {({ id }) => (
                <Combobox
                  id={id}
                  disabled={busy}
                  value={triValue(value.anonymous)}
                  onChange={(next) => onChange({ anonymous: triState(next) })}
                  options={ANONYMOUS_OPTIONS}
                  emptyOptionLabel="Anyone"
                  placeholder="Anyone"
                />
              )}
            </Field>
            {/*
              * The deleted-account escape hatch, and the reason the Who picker
              * upstairs is not the whole story. A deleted user's `user_id` is
              * nulled while `actor_email` survives, so their history is reachable
              * by email and by nothing else — the picker lists live staff and
              * cannot offer somebody who no longer has a row.
              *
              * Setting this clears the picker and vice versa, so the pair that
              * would otherwise AND to an empty feed cannot both be set.
              */}
            <TextFilter
              label="Email of a deleted account"
              type="search"
              value={value.actorEmail}
              disabled={busy}
              onCommit={commitActorEmail}
              leadingIcon={<SearchIcon />}
            />
          </FilterGroup>

          <FilterGroup title="Speed">
            <NumberFilter
              label="Slower than (ms)"
              value={value.minDurationMs}
              disabled={busy}
              onCommit={(next) => onChange({ minDurationMs: next })}
            />
          </FilterGroup>

          <FilterGroup title="Technical">
            {/*
              * `method` lives here, unmerged and honestly labelled. See the note
              * on the "What happened" control: that one writes `actionSlug` and
              * this one writes `method`, they are separate params, and neither
              * handler touches the other's key.
              */}
            <Field label="HTTP method">
              {({ id }) => (
                <MultiCombobox
                  id={id}
                  disabled={busy}
                  values={listOf(value.method)}
                  onChange={(next) => onChange({ method: csvOrUndefined(next) })}
                  options={METHOD_OPTIONS}
                  placeholder="Any method"
                  searchPlaceholder="Search methods…"
                  summaryNoun={{ singular: 'method', plural: 'methods' }}
                />
              )}
            </Field>
            <TextFilter
              label="Web address contains"
              type="search"
              value={value.path}
              disabled={busy}
              onCommit={(next) => onChange({ path: next })}
              leadingIcon={<SearchIcon />}
            />
            <TextFilter
              label="Exact endpoint"
              value={value.route}
              disabled={busy}
              onCommit={(next) => onChange({ route: next })}
            />
          </FilterGroup>

          <FilterGroup title="Support">
            <TextFilter
              label="Support reference"
              value={value.requestId}
              disabled={busy}
              onCommit={(next) => onChange({ requestId: next })}
            />
            <TextFilter
              label="Error code"
              value={value.errorCode}
              disabled={busy}
              onCommit={(next) => onChange({ errorCode: next })}
            />
          </FilterGroup>

          <FilterGroup title="Order">
            <Field label="Sort by">
              {({ id }) => (
                <Combobox
                  id={id}
                  disabled={busy}
                  value={value.sort ?? ''}
                  onChange={(next) =>
                    onChange({ sort: next === '' ? undefined : (next as ApiLogSort) })
                  }
                  options={SORT_OPTIONS}
                  emptyOptionLabel="Default"
                  placeholder="Default"
                />
              )}
            </Field>
            <Field label="Direction">
              {({ id }) => (
                <Combobox
                  id={id}
                  disabled={busy}
                  value={value.order ?? ''}
                  onChange={(next) =>
                    onChange({ order: next === '' ? undefined : (next as 'asc' | 'desc') })
                  }
                  options={DIRECTION_OPTIONS}
                  emptyOptionLabel="Default"
                  placeholder="Default"
                />
              )}
            </Field>
          </FilterGroup>
        </div>
      )}
    </div>
  );
}

/** A named block inside the disclosure. `min-w-0` so a wide value cannot widen its grid track. */
function FilterGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="grid min-w-0 content-start gap-2.5">
      <h3 className="text-xs font-semibold text-fg-secondary">{title}</h3>
      {children}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-4 w-4"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-3.5 w-3.5 shrink-0"
    >
      <path d="m5 13 4 4L19 7" />
    </svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx(
        'h-4 w-4 shrink-0 transition-transform duration-150 ease-out motion-reduce:transition-none',
        open && 'rotate-180',
      )}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
